import * as vp from 'vopay';
import { Box, Select, TextField, DateRangePicker } from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from './ScheduledTransactions.module.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';

type ScheduledClientAccountTransactionSearchParams = {
  transferType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  transferType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  onSearch: (searchParams: ScheduledClientAccountTransactionSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const ScheduledTransactionsForm: FC = (props: SearchFormProps) => {
  const {
    transferType,
    frequency,
    status,
    searchTerm,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    initialTimeRange,
    onSearch,
  } = props;

  const [currentTransferType, setCurrentTransferType] = useState<string>(transferType);
  const [currentFrequency, setCurrentFrequency] = useState<string>(frequency);
  const [currentStatus, setCurrentStatus] = useState<string>(status);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.Schedule' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const transferTypeOption = [
    { label: tGlobal('TransferTypeOptions.all'), value: 'all' },
    { label: tGlobal('TransferTypeOptions.fundTransfer'), value: 'fund-transfer' },
    { label: tGlobal('TransferTypeOptions.transferWithdraw'), value: 'transfer-withdraw' },
    { label: tGlobal('TransferTypeOptions.fundTransferWithdraw'), value: 'fund-transfer-withdraw' },
  ];

  const frequencyOptions = [
    { label: tGlobal('FrequencyOptions.all'), value: 'all' },
    { label: tGlobal('FrequencyOptions.single'), value: 'single' },
    { label: tGlobal('FrequencyOptions.monthly'), value: 'monthly' },
    { label: tGlobal('FrequencyOptions.weekly'), value: 'weekly' },
    { label: tGlobal('FrequencyOptions.semiMonthly'), value: 'semi-monthly' },
    { label: tGlobal('FrequencyOptions.annually'), value: 'yearly' },
  ];

  const statusOptions = [
    { label: t('SearchForm.Status.Options.all'), value: 'all' },
    { label: t('SearchForm.Status.Options.cancelled'), value: 'cancelled' },
    { label: t('SearchForm.Status.Options.scheduled'), value: 'scheduled' },
    { label: t('SearchForm.Status.Options.completed'), value: 'completed' },
    { label: t('SearchForm.Status.Options.inProgress'), value: 'in progress' },
  ];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSearch({
          transferType: currentTransferType,
          searchTerm: currentSearchTerm,
          frequency: currentFrequency,
          status: currentStatus,
          timeRange,
        });
      }}
    >
      <Box flx="row" gap="sm" wrap="wrap" ai="center">
        <div className={styles.datePickerContainer}>
          <DateRangePicker
            onChange={(range) => {
              setTimeRange(range);
            }}
            triggerVariant="outlineShadow"
            initialTime={timeRange}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
          />
        </div>
        <Box width="200px">
          <Select
            version="2"
            placeholder={t('SearchForm.transferTypePlaceholder')}
            options={transferTypeOption}
            name="scheduleType"
            value={currentTransferType}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentTransferType(e.target.value);
            }}
            sort={false}
          />
        </Box>
        <Box width="200px">
          <Select
            version="2"
            placeholder={t('SearchForm.frequencyPlaceholder')}
            options={frequencyOptions}
            name="frequency"
            value={currentFrequency}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentFrequency(e.target.value);
            }}
            sort={false}
          />
        </Box>
        <Box width="180px">
          <Select
            version="2"
            placeholder={t('SearchForm.Status.placeholder')}
            options={statusOptions}
            name="status"
            value={currentStatus}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentStatus(e.target.value);
            }}
            sort={false}
          />
        </Box>
        <Box maxwidth="180px">
          <TextField
            key="AccountSearchKeyword"
            name="AccountSearchKeyword"
            placeholder={t('SearchForm.keywordPlaceholder')}
            value={currentSearchTerm}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentSearchTerm(e.target.value);
            }}
            variant="outlineShadow"
          />
        </Box>
        <Button type="submit" id="search-schedule-transactions" size="large" colorScheme="primary">
          {t('SearchForm.submitButton')}
        </Button>
      </Box>
    </form>
  );
};

export default ScheduledTransactionsForm;
